import { find } from 'lodash-es'

const lineLikeElementGuid = (element: { guid: string; element_guid?: string }) =>
  element.element_guid ?? element.guid

const positionGuid = (position: Position) => position.position_guid

const getWallTimberFrameLayer = (
  assembly: Assembly | undefined,
): TimberFrameLayerWall | undefined => {
  if (!assembly) return undefined
  const layer = find(assembly.layers, layerItem => 'standard_rip_cs' in layerItem)
  return layer as TimberFrameLayerWall | undefined
}

const resolveAssemblyStandardCrossSection = (position: Position): CrossSection | undefined => {
  const timberFrame = getWallTimberFrameLayer(position.wallAssembly)
  if (!timberFrame) return undefined
  return position.element_type === 'lintels' ? timberFrame.lintel_cs : timberFrame.standard_rip_cs
}

const resolvePositionCrossSection = (position: Position): CrossSection | undefined => {
  if (position.crossSection) return position.crossSection
  if (position.crossSectionPolicy === 'UserDefined' && position.userDefinedCrossSection) {
    return position.userDefinedCrossSection
  }
  if (position.crossSectionPolicy === 'AssemblyStandard') {
    return resolveAssemblyStandardCrossSection(position)
  }
  return undefined
}

/** Assembly-standard CS for a rip/lintel, ignoring the element's current policy. */
export const resolveAssemblyStandardCrossSectionFromModel = (
  model: PlanarModel,
  elementGuid: string,
): CrossSection | undefined => {
  const position =
    model.rips.find(rip => rip.position_guid === elementGuid) ??
    model.lintels.find(lintel => lintel.position_guid === elementGuid)
  if (!position) return undefined
  return resolveAssemblyStandardCrossSection(position)
}

/** Derive ElementCSAssignment[] from planar model (CS lives on elements). */
export const deriveElementCrossSectionAssignment = (model: PlanarModel): ElementCSAssignment[] => {
  const fromLineLike = (elements: ShapeObjectLine[]): ElementCSAssignment[] =>
    elements.flatMap(element => {
      if (!element.crossSection) return []
      return [
        {
          element_guid: lineLikeElementGuid(element),
          element_cs: element.crossSection,
        },
      ]
    })

  const fromPositions = (positions: Position[]): ElementCSAssignment[] =>
    positions.flatMap(position => {
      const crossSection = resolvePositionCrossSection(position)
      if (!crossSection) return []
      return [
        {
          element_guid: positionGuid(position),
          element_cs: crossSection,
          crossSectionPolicy: position.crossSectionPolicy,
        },
      ]
    })

  return [
    ...fromLineLike(model.beams),
    ...fromLineLike(model.columns),
    ...fromLineLike(model.purlins),
    ...fromPositions(model.rips),
    ...fromPositions(model.lintels),
  ]
}

/** Derive AssemblyAssignment[] from planar model (assembly lives on elements). */
export const deriveAssemblyAssignment = (model: PlanarModel): AssemblyAssignment[] => {
  const fromHosts = (
    elements: Array<{ guid: string; assembly?: Assembly }>,
  ): AssemblyAssignment[] =>
    elements.flatMap(element => {
      if (!element.assembly?.guid) return []
      return [
        {
          element_guid: element.guid,
          assembly_guid: element.assembly.guid,
        },
      ]
    })

  return [
    ...fromHosts(model.walls),
    ...fromHosts(model.vertical_slabs),
    ...fromHosts(model.vertical_roof_slabs),
  ]
}
